import React, { useState, useEffect } from 'react';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';
import { LiquidGlassCard, NestedCard } from './LiquidGlass/index';
import { useTheme } from '../contexts/ThemeContext';
import { solidWhenReducedTransparency } from '../styles/reducedTransparency';
import { scrollToSection } from '../utils/scrollToSection';

// PRIMARY BASE LAYER - Header wrapper like About component
const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  padding: 8px;
  background-color: transparent;
  pointer-events: none;
  transform: translateY(${props => props.isVisible ? '0' : '-100%'});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    padding: 3px;
  }
`;

// LIQUID GLASS LAYER - Header container with enhanced transparency
//
// DWEB-171: this used to FORCE its tint —
//   background: rgba(30,58,138,.3) !important;
//   border: 1px solid rgba(59,130,246,.4) !important;
// The defect was never the colour. It was that the header held its own private
// copy of a value the buttons also hold, and forced it past the theme system
// with `!important`, so nothing could reach it. The `!important` was cargo on
// top: HeaderContainer is styled(LiquidGlassCard), so its own declaration
// already won on source order over LiquidGlassCardStyled's plain `background`.
// Nothing ever needed forcing.
//
// The VALUE here is the owner's call, taken mid-release on 2026-09-08 after
// seeing it on the new ground: keep the blue, and make the header match the
// buttons rather than diverge from them. So these are LiquidGlassButton.js's
// own two values — deliberately the same numbers, not a coincidence — reached
// through a custom property instead of being forced.
//
// DWEB-172 (v6.8.0) CLOSED THE COPY. --glass-bg and --glass-border are now
// declared in exactly one place, src/index.css, and this file states neither
// value — not even as a var() fallback, because a fallback IS the second copy.
// The tint turned out to live in FIVE files, not the two the ticket named;
// see the census in index.css. The values are unchanged: the release gate was
// computed backgroundColor/borderColor here, on the mobile nav and on a
// button in both states, before and after, required identical.
//
// --glass-blur / --glass-saturation are still undeclared and still resolve to
// the fallbacks below, DELIBERATELY: Footer.js falls back to 300% saturation
// where this file falls back to 240%, so one declared value would change one
// of them. That is a design decision, not a refactor. Do not "finish the job"
// by declaring them without deciding which saturation the site wants.
//
// saturate() is 240%, not the 140% DWEB-171 specifies: 140% was reasoned from
// a NEUTRAL header on a neutral ground, where there is no chroma to boost.
// A blue tint is chroma, so that reasoning no longer applies to this header.
const HeaderContainer = styled(LiquidGlassCard)`
  max-width: 1200px;
  margin: 0 auto;
  padding: 6px 12px;
  min-height: 36px;
  display: flex;
  align-items: center;
  pointer-events: auto;
  background: var(--glass-bg);
  backdrop-filter: blur(var(--glass-blur, 35px)) saturate(var(--glass-saturation, 240%));
  -webkit-backdrop-filter: blur(var(--glass-blur, 35px)) saturate(var(--glass-saturation, 240%));
  border: 1px solid var(--glass-border);
  border-radius: 18px;
  
  @media (max-width: 768px) {
    padding: 3px 6px;
    min-height: 28px;
    border-radius: 14px;
  }

  /* DWEB-173: opaque when the visitor has asked for less transparency. */
  ${solidWhenReducedTransparency('var(--glass-bg-solid)')}
`;


// NESTED ELEMENT 1: Logo as liquid glass nested card
const LogoCard = styled.div`
  padding: 6px 12px;
  margin-right: auto;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;
  border: none;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    display: block;
    padding: 1px 3px;
    margin-right: auto;
  }
`;

const LogoText = styled.div`
  color: white;
  font-size: 1.1rem;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6), 0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4);
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    letter-spacing: 0.3px;
  }
`;

// NESTED ELEMENT 2: Menu button as liquid glass nested card
const MenuButtonCard = styled(NestedCard)`
  display: none;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    display: flex;
    width: 24px;
    height: 24px;
    border-radius: 12px;
  }
`;

const MenuButtonIcon = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6), 0 0 10px rgba(30, 64, 175, 0.6);

  &:hover {
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6), 0 0 15px rgba(30, 64, 175, 0.8), 0 0 25px rgba(30, 64, 175, 0.5);
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

// NESTED ELEMENT 3: Menu list as liquid glass nested card
const MenuListCard = styled.div`
  display: flex;
  padding: 12px 16px;
  gap: 1rem;
  border-radius: 28px;
  margin: 0;
  background: transparent;
  border: none;

  /*
   * DWEB-53: on mobile this is simply HIDDEN. It used to become a second,
   * fully styled dropdown gated on the same isMobileMenuOpen state as
   * MobileMenuContainer, so one tap of the hamburger opened BOTH. Measured at
   * 390x844 they overlapped almost completely — two stacked translucent panels
   * with two copies of every item, and every label twice in the accessibility
   * tree.
   *
   * MobileMenuContainer is the intended mobile menu (it has the overlay, the
   * close-on-tap handlers and the < 480px sizing); this @media block was an
   * earlier approach that was never removed. MenuListCard itself stays — it is
   * the DESKTOP nav -- the ul-li-a list -- so it cannot be deleted outright.
   * What is gone is ~35 lines of duplicate open-state styling and the
   * slideDown keyframes only that state used.
   */
  @media (max-width: 768px) {
    display: none;
  }
`;

const MenuList = styled.ul`
  display: contents;
  list-style: none;
  margin: 0;
  padding: 0;
`;

// NESTED ELEMENT 4: Menu items as liquid glass nested cards
const MenuItemCard = styled.div`
  position: relative;
  transition: all 0.3s ease;
  padding: 8px 16px;
  border-radius: 28px;
  cursor: pointer;
  background: transparent;
  border: none;
  box-shadow: none;

  &:hover {
    background: rgba(59, 130, 246, 0.08);
    backdrop-filter: blur(15px);
    box-shadow: 0 8px 32px rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.2);
  }

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    padding: 12px;
    
    &:hover {
      transform: translateX(5px);
      background: rgba(59, 130, 246, 0.08);
      backdrop-filter: blur(15px);
      box-shadow: 0 8px 32px rgba(59, 130, 246, 0.1);
      border: 1px solid rgba(59, 130, 246, 0.2);
    }
  }

  @media (min-width: 769px) {
    &::after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 0;
      height: 2px;
      background-color: #1E40AF;
      transition: width 0.3s ease;
    }

    &:hover::after {
      width: 100%;
    }
  }
`;

const MenuItem = styled.li`
  display: contents;
`;

// Mobile Menu Components - Optimized with liquid glass standardization
const MobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 998;
  display: ${props => props.isOpen ? 'block' : 'none'};
  backdrop-filter: blur(4px);
  opacity: ${props => props.isOpen ? 1 : 0};
  transition: opacity 0.3s ease;
`;

// DWEB-171 (second instance): MobileMenuContainer carried the SAME hardcoded
// navy pair and the same two `!important`s. The ticket and the design note
// name HeaderContainer only — both say "the only !important in the glass
// layer", which is not what the file says. This is the nav at <=768px, so
// fixing the desktop bar alone would have shipped a blue nav on phones for
// exactly the reason DWEB-171 exists. Fixed here and flagged in the report.
//
// DWEB-172 (v6.8.0) then found the same undercount one level up — the ticket
// said the tint lived in two files and it lived in five. Twice now, a ticket
// has named a count nobody had grepped for. Grep first.
const MobileMenuContainer = styled(LiquidGlassCard)`
  position: fixed;
  top: 38px;
  right: 15px;
  width: 260px;
  max-height: calc(100vh - 50px);
  z-index: 999;
  /*
   * DWEB-53: the media query is new. This had NONE, and was kept off desktop
   * only by the hamburger being display:none there — incidental, not
   * structural. Now MenuListCard is the desktop nav and this is the mobile
   * nav, each explicitly bounded, so neither can leak into the other's
   * viewport.
   */
  display: none;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
  }
  animation: ${props => props.isOpen ? 'slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'};
  padding: 0;
  border-radius: 24px;
  overflow: hidden;
  background: var(--glass-bg);
  backdrop-filter: blur(var(--glass-blur, 35px)) saturate(var(--glass-saturation, 240%));
  -webkit-backdrop-filter: blur(var(--glass-blur, 35px)) saturate(var(--glass-saturation, 240%));
  border: 1px solid var(--glass-border);
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(100%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
  }
  
  @media (max-width: 480px) {
    width: calc(100vw - 30px);
    right: 15px;
    left: 15px;
    transform: none;
  }

  /* DWEB-173: the mobile nav is the same surface as the bar; same fallback. */
  ${solidWhenReducedTransparency('var(--glass-bg-solid)')}
`;

const MobileMenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px;
`;

const MobileMenuItem = styled(NestedCard)`
  padding: 12px 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  text-align: center;
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  margin: 0;
  
  &:hover {
    background: rgba(30, 64, 175, 0.15);
    border-color: rgba(30, 64, 175, 0.3);
    transform: translateX(3px);
    box-shadow: 0 4px 12px rgba(30, 64, 175, 0.2);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: #1E40AF;
    transform: scaleY(0);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: bottom;
    border-radius: 0 2px 2px 0;
  }
  
  &:hover::before {
    transform: scaleY(1);
  }
`;

const MobileMenuLink = styled.div`
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.3s ease;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6), 0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4);
  
  ${MobileMenuItem}:hover & {
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6), 0 0 20px rgba(30, 64, 175, 1), 0 0 40px rgba(30, 64, 175, 0.6);
  }
`;

function Header() {
  const [isVisible, setIsVisible] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDarkMode } = useTheme();
  
  const backgroundImage = null; // Simplified - no background image

  useEffect(() => {
    let hideTimer = null;
    let isAtTop = true;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      isAtTop = scrollTop < 50; // Consider "at top" if within 50px of top
      
      if (isAtTop) {
        // Always show header when at top of page
        setIsVisible(true);
        clearTimeout(hideTimer);
      } else {
        setIsVisible(false);
        clearTimeout(hideTimer);
      }
    };

    const handleMouseMove = (e) => {

      const { clientY, clientX } = e;
      const windowWidth = window.innerWidth;
      const topMiddleZone = {
        top: 0,
        bottom: 100, // Top 100px of screen
        left: windowWidth * 0.3, // Middle 40% of screen width
        right: windowWidth * 0.7
      };

      // Show header if mouse is in top middle zone or page is at top
      if ((clientY >= topMiddleZone.top && 
           clientY <= topMiddleZone.bottom && 
           clientX >= topMiddleZone.left && 
           clientX <= topMiddleZone.right) || isAtTop) {
        setIsVisible(true);
        
        // Clear existing timer and set new one
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => {
          if (!isAtTop && !isMobileMenuOpen) {
            setIsVisible(false);
          }
        }, 2000);
      }
    };

    // Initial scroll check
    handleScroll();

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(hideTimer);
    };
  }, [isMobileMenuOpen]);

  const toggleMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Keep header visible when mobile menu is open
    if (!isMobileMenuOpen) {
      setIsVisible(true);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <HeaderWrapper
        isVisible={isVisible}
        style={{
          ...(backgroundImage && {
            backgroundImage: `linear-gradient(${isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.1)'}, ${isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.1)'}), url('${backgroundImage}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            backgroundRepeat: 'no-repeat'
          })
        }}
      >
        <HeaderContainer variant="dark">
          {/* Logo as nested liquid glass card */}
          {/* Real anchor so the logo is a crawlable route to "/" (review
              finding S1); the onClick stays for the existing behaviour. */}
          <LogoCard onClick={() => window.location.href = '/'}>
            <a
              href="/"
              aria-label="DynamWorks home"
              style={{ color: 'inherit', textDecoration: 'none' }}
              onClick={(e) => e.preventDefault()}
            >
              <LogoText>DynamWorks</LogoText>
            </a>
          </LogoCard>
          
          {/* Menu list as nested liquid glass card */}
          {/* No isOpen: since DWEB-53 this is the desktop nav only and is
              display:none under 768px. MobileMenuContainer below is the
              one mobile menu. */}
          <MenuListCard>
            <MenuList>
              <MenuItem>
                <MenuItemCard>
                  <a
                    href="/"
                    onClick={(e) => {
                      e.preventDefault();
                      closeMobileMenu();
                      if (window.location.pathname !== '/') {
                        window.location.href = '/';
                      } else {
                        scrollToSection('hero');
                      }
                    }}
                    style={{ 
                      color: 'white', 
                      textDecoration: 'none', 
                      cursor: 'pointer',
                      textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6), 0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                    }}
                  >
                    Home
                  </a>
                </MenuItemCard>
              </MenuItem>
              <MenuItem>
                <MenuItemCard>
                  <a
                    href="/#about"
                    onClick={(e) => {
                      e.preventDefault();
                      closeMobileMenu();
                      if (window.location.pathname !== '/') {
                        // Store the target section in sessionStorage and redirect with hash
                        sessionStorage.setItem('scrollTarget', 'about');
                        window.location.href = '/#about';
                      } else {
                        scrollToSection('about');
                      }
                    }}
                    style={{ 
                      color: 'white', 
                      textDecoration: 'none', 
                      cursor: 'pointer',
                      textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                    }}
                  >
                    About
                  </a>
                </MenuItemCard>
              </MenuItem>
              <MenuItem>
                <MenuItemCard>
                  <a
                    href="/#app-demos"
                    onClick={(e) => {
                      e.preventDefault();
                      closeMobileMenu();
                      if (window.location.pathname !== '/') {
                        sessionStorage.setItem('scrollTarget', 'app-demos');
                        window.location.href = '/#app-demos';
                      } else {
                        scrollToSection('app-demos');
                      }
                    }}
                    style={{ 
                      color: 'white', 
                      textDecoration: 'none', 
                      cursor: 'pointer',
                      textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                    }}
                  >
                    Apps
                  </a>
                </MenuItemCard>
              </MenuItem>
              <MenuItem>
                <MenuItemCard>
                  <a
                    href="/#services"
                    onClick={(e) => {
                      e.preventDefault();
                      closeMobileMenu();
                      if (window.location.pathname !== '/') {
                        sessionStorage.setItem('scrollTarget', 'services');
                        window.location.href = '/#services';
                      } else {
                        scrollToSection('services');
                      }
                    }}
                    style={{ 
                      color: 'white', 
                      textDecoration: 'none', 
                      cursor: 'pointer',
                      textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                    }}
                  >
                    Services
                  </a>
                </MenuItemCard>
              </MenuItem>
              <MenuItem>
                <MenuItemCard>
                  <a
                    href="/#faq"
                    onClick={(e) => {
                      e.preventDefault();
                      closeMobileMenu();
                      if (window.location.pathname !== '/') {
                        sessionStorage.setItem('scrollTarget', 'faq');
                        window.location.href = '/#faq';
                      } else {
                        scrollToSection('faq');
                      }
                    }}
                    style={{ 
                      color: 'white', 
                      textDecoration: 'none', 
                      cursor: 'pointer',
                      textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                    }}
                  >
                    FAQ
                  </a>
                </MenuItemCard>
              </MenuItem>
              <MenuItem>
                <MenuItemCard>
                  <RouterLink 
                    to="/blog" 
                    onClick={closeMobileMenu} 
                    style={{ 
                      color: 'white', 
                      textDecoration: 'none',
                      textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                    }}
                  >
                    Blog
                  </RouterLink>
                </MenuItemCard>
              </MenuItem>
              <MenuItem>
                <MenuItemCard>
                  <a
                    href="/#contact"
                    onClick={(e) => {
                      e.preventDefault();
                      closeMobileMenu();
                      if (window.location.pathname !== '/') {
                        sessionStorage.setItem('scrollTarget', 'contact');
                        window.location.href = '/#contact';
                      } else {
                        scrollToSection('contact');
                      }
                    }}
                    style={{ 
                      color: 'white', 
                      textDecoration: 'none', 
                      cursor: 'pointer',
                      textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                    }}
                  >
                    Contact
                  </a>
                </MenuItemCard>
              </MenuItem>
            </MenuList>
          </MenuListCard>
          
          {/* Menu button as nested liquid glass card */}
          <MenuButtonCard variant="default" glassVariant="dark">
            <MenuButtonIcon onClick={toggleMenu}>
              {isMobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
            </MenuButtonIcon>
          </MenuButtonCard>
        </HeaderContainer>
      </HeaderWrapper>

      {/* Mobile Menu - Optimized */}
      <MobileMenuOverlay isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />
      <MobileMenuContainer variant="dark" isOpen={isMobileMenuOpen}>
        <MobileMenuList>
          <MobileMenuItem variant="default" glassVariant="dark">
            <MobileMenuLink>
              <a
                    href="/"
                onClick={(e) => {
                      e.preventDefault();
                  closeMobileMenu();
                  if (window.location.pathname !== '/') {
                    window.location.href = '/';
                  } else {
                    scrollToSection('hero');
                  }
                }}
                style={{ 
                  color: 'white', 
                  textDecoration: 'none', 
                  cursor: 'pointer',
                  textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                }}
              >
                Home
              </a>
            </MobileMenuLink>
          </MobileMenuItem>
          <MobileMenuItem variant="default" glassVariant="dark">
            <MobileMenuLink>
              <a
                    href="/#about"
                onClick={(e) => {
                      e.preventDefault();
                  closeMobileMenu();
                  if (window.location.pathname !== '/') {
                    sessionStorage.setItem('scrollTarget', 'about');
                    window.location.href = '/#about';
                  } else {
                    scrollToSection('about');
                  }
                }}
                style={{ 
                  color: 'white', 
                  textDecoration: 'none', 
                  cursor: 'pointer',
                  textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                }}
              >
                About
              </a>
            </MobileMenuLink>
          </MobileMenuItem>
          <MobileMenuItem variant="default" glassVariant="dark">
            <MobileMenuLink>
              <a
                    href="/#app-demos"
                onClick={(e) => {
                      e.preventDefault();
                  closeMobileMenu();
                  if (window.location.pathname !== '/') {
                    sessionStorage.setItem('scrollTarget', 'app-demos');
                    window.location.href = '/#app-demos';
                  } else {
                    scrollToSection('app-demos');
                  }
                }}
                style={{ 
                  color: 'white', 
                  textDecoration: 'none', 
                  cursor: 'pointer',
                  textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                }}
              >
                Apps
              </a>
            </MobileMenuLink>
          </MobileMenuItem>
          <MobileMenuItem variant="default" glassVariant="dark">
            <MobileMenuLink>
              <a
                    href="/#services"
                onClick={(e) => {
                      e.preventDefault();
                  closeMobileMenu();
                  if (window.location.pathname !== '/') {
                    sessionStorage.setItem('scrollTarget', 'services');
                    window.location.href = '/#services';
                  } else {
                    scrollToSection('services');
                  }
                }}
                style={{ 
                  color: 'white', 
                  textDecoration: 'none', 
                  cursor: 'pointer',
                  textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                }}
              >
                Services
              </a>
            </MobileMenuLink>
          </MobileMenuItem>
          <MobileMenuItem variant="default" glassVariant="dark">
            <MobileMenuLink>
              <a
                    href="/#faq"
                onClick={(e) => {
                      e.preventDefault();
                  closeMobileMenu();
                  if (window.location.pathname !== '/') {
                    sessionStorage.setItem('scrollTarget', 'faq');
                    window.location.href = '/#faq';
                  } else {
                    scrollToSection('faq');
                  }
                }}
                style={{ 
                  color: 'white', 
                  textDecoration: 'none', 
                  cursor: 'pointer',
                  textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                }}
              >
                FAQ
              </a>
            </MobileMenuLink>
          </MobileMenuItem>
          <MobileMenuItem variant="default" glassVariant="dark">
            <MobileMenuLink>
              <RouterLink 
                to="/blog" 
                onClick={closeMobileMenu} 
                style={{ 
                  color: 'white', 
                  textDecoration: 'none',
                  textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                }}
              >
                Blog
              </RouterLink>
            </MobileMenuLink>
          </MobileMenuItem>
          <MobileMenuItem variant="default" glassVariant="dark">
            <MobileMenuLink>
              <a
                    href="/#contact"
                onClick={(e) => {
                      e.preventDefault();
                  closeMobileMenu();
                  if (window.location.pathname !== '/') {
                    sessionStorage.setItem('scrollTarget', 'contact');
                    window.location.href = '/#contact';
                  } else {
                    scrollToSection('contact');
                  }
                }}
                style={{ 
                  color: 'white', 
                  textDecoration: 'none', 
                  cursor: 'pointer',
                  textShadow: '0 0 15px rgba(30, 64, 175, 0.8), 0 0 30px rgba(30, 64, 175, 0.4)'
                }}
              >
                Contact
              </a>
            </MobileMenuLink>
          </MobileMenuItem>
        </MobileMenuList>
      </MobileMenuContainer>
    </>
  );
}

export default Header;
